import type { Locale } from 'date-fns';
import {
  differenceInCalendarWeeks,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  isMatch,
  isSameDay,
  isValid,
  parse,
  startOfDay,
  sub,
} from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';

import { DAY_NUMBER_MAP } from 'watchtower-ui/types/date';
import type { TDay } from 'watchtower-ui/types/date';

const defaultDateFormat = 'yyyy-MM-dd';

export const defaultMonthFormat = 'MMM-yyyy';

export const formatDate = (date: Date, dateFormat: string = defaultDateFormat) => format(date, dateFormat);

export const isSaturday = (date: Date): boolean => date.getDay() === 6;

const convertLocalDateToUTCIgnoringTimezone = (date: Date) =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ),
  );

// Map of supported locales
export const supportedLocales: { [key: string]: Locale } = {
  'en-US': enUS,
  'pt-BR': ptBR,
};

// Helper to get the best matching browser locale
export const getBrowserLocale = (): string => {
  const browserLocale = navigator.languages?.[0] || navigator.language || 'en-US';
  // Use exact match if available; otherwise, match language part.
  if (supportedLocales[browserLocale]) return browserLocale;
  const languageOnly = browserLocale.split('-')[0];
  for (const key in supportedLocales) {
    if (languageOnly && key.startsWith(languageOnly)) return key;
  }
  return 'en-US';
};

export const parseDateString = (dateString: string, moveToUTC?: boolean): Date | null => {
  let formattedDateString = dateString;

  // Check if dateString is in the format 'MMM-yyyy', and if so, convert it to the last day of the month
  if (isMatch(dateString, defaultMonthFormat)) {
    formattedDateString = formatDate(endOfMonth(parse(dateString, defaultMonthFormat, new Date())));
  }

  const delimiter = dateString.includes('/') ? '/' : '-';
  const [first, second, third] = formattedDateString.split(delimiter).map((s) => Number.parseInt(s, 10));

  let year;
  let month;
  let day;
  if (delimiter === '/') {
    [month, day, year] = [first, second, third];
  } else {
    [year, month, day] = [first, second, third];
  }

  // Check if year, month, and day are valid integers
  if (!Number.isSafeInteger(year) || !Number.isSafeInteger(month) || !Number.isSafeInteger(day)) return null;

  // Ensure year, month, and day are not undefined before creating a new Date
  if (year === undefined || month === undefined || day === undefined) return null;

  const date = new Date(year, month - 1, day);
  if (moveToUTC) {
    return convertLocalDateToUTCIgnoringTimezone(date);
  } else {
    return date;
  }
};

export const getFirstWeek = (selectedDate: Date) => {
  // We are copying the date so we can modify it.
  const result = new Date(selectedDate);
  result.setMonth(0, 1);
  result.setDate(7 - result.getDay());
  return result;
};

export const yearBackDay = (endDate: Date, weeklyOnly: boolean = true) => {
  const date = sub(endDate, { years: 1 });
  if (!weeklyOnly) return date;
  const lastDay = date.getDate() - (date.getDay() - 1) + 5;
  return new Date(date.setDate(lastDay));
};

// made the changes as the test should pass in every time zone, so date will be coneverted into UTC and then checked
export const isDateInRange = (date: Date, minDate: Date, maxDate: Date, weeklyOnly: boolean = true): boolean => {
  const utcDate = startOfDay(date).getTime();
  const utcMinDate = startOfDay(minDate).getTime();
  const utcMaxDate = startOfDay(maxDate).getTime();

  return (
    isSameDay(date, minDate) ||
    isSameDay(date, maxDate) ||
    (isAfter(utcDate, utcMinDate) && isBefore(utcDate, utcMaxDate) && (weeklyOnly ? isSaturday(date) : true))
  );
};
export const isValidDate = (date: unknown): date is Date => isValid(date) && date instanceof Date;
export const getPreviousSaturday = (date: Date, weeklyOnly: boolean = true): Date => {
  if (!weeklyOnly) return new Date(date);
  const dayOfWeek = date.getDay();
  const daysToSubtract = (dayOfWeek + 1) % 7;
  const previousSaturday = new Date(date);
  previousSaturday.setDate(date.getDate() - daysToSubtract);
  return previousSaturday;
};

export const checkDiffInWeeks = (startDate: Date | undefined, endDate: Date | undefined): number | undefined => {
  if (startDate && endDate) {
    // here 1 we are adding because we are comparing with saturday's so everytime it will one less count
    return differenceInCalendarWeeks(endDate, startDate) + 1;
  }
  return undefined;
};

export const maskTimeBlockFromStartDay = (
  startDay: TDay | null | undefined,
  dateRange: [Date | string, Date | string] | null | undefined,
): [Date, Date] => {
  if (!dateRange) return [new Date(), new Date()];
  const [startDate, endDate] = dateRange;
  // Use parseDateString for string inputs to avoid UTC-midnight timezone shift
  const maskedStartDate =
    typeof startDate === 'string' ? parseDateString(startDate) ?? new Date(startDate) : new Date(startDate);
  const maskedEndDate = typeof endDate === 'string' ? parseDateString(endDate) ?? new Date(endDate) : new Date(endDate);

  if (!startDay) {
    return [maskedStartDate, maskedEndDate];
  }

  const targetDayNumber = DAY_NUMBER_MAP[startDay];
  const firstSaturday = getPreviousSaturday(maskedStartDate);
  const lastSaturday = getPreviousSaturday(maskedEndDate);

  // We start ranging between first and last saturday from the given date range
  // Then we work back to match the starting day block.
  if (isBefore(firstSaturday, maskedStartDate)) {
    maskedStartDate.setTime(firstSaturday.getTime());
  }

  if (isAfter(lastSaturday, maskedEndDate)) {
    maskedEndDate.setTime(lastSaturday.getTime());
  }

  // Mask the start date
  const daysBack = (maskedStartDate.getDay() - targetDayNumber + 7) % 7;
  maskedStartDate.setDate(maskedStartDate.getDate() - daysBack);

  // Mask the end date
  const daysForward = (((targetDayNumber + 6) % 7) - maskedEndDate.getDay() + 7) % 7;
  maskedEndDate.setDate(maskedEndDate.getDate() + daysForward);

  return [maskedStartDate, maskedEndDate];
};
